import { KeyboardArrowLeft, Send, Image, Close } from "@mui/icons-material";
import { Box, Divider, InputBase, Stack, IconButton, Typography } from "@mui/material";
import { employeeDayNotesApi } from "api";
import { EmployeeDayNoteEntity } from "api/generated";
import FlexBetween from "components/flexbox/FlexBetween";
import Scrollbar from "components/ScrollBar";
import { StyledIconButton } from "components/TenantDisplay";
import { Small } from "components/Typography";
import ShiftNotesBubble from "page-sections/notes/ShiftNotesBubble";
import React, { ChangeEvent, useEffect, FC, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  notes: EmployeeDayNoteEntity[];
  employeeId: number;
  date: Date;
  fetchData: () => void;
  // Passed as query params so DEPCHIEF users pass the backend permission guard.
  departmentId?: number;
  seasonId?: number;
}

// Inline (non-modal) thread of manager-only notes about a single employee on a
// single day. Rendered inside the Add-Shift modal.
const EmployeeDayNotesSection: FC<Props> = ({ notes, employeeId, date, fetchData, departmentId, seasonId }) => {
  const [note, setNote] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const notesEnd = React.useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [noteActionId, setNoteActionId] = useState(-1);
  const [disableHover, setDisableHover] = useState(false);
  const { t } = useTranslation();

  const authParams = { params: { departmentId, seasonId } };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const createNote = () => {
    // Serialize the LOCAL calendar day (not the UTC day). Using setUTCHours here
    // would roll back a day for timezones ahead of UTC (e.g. CEST midnight is the
    // previous day in UTC), which stored/showed the note one day early.
    const isoDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
    if (selectedImage) {
      const formData = new FormData();
      formData.append('text', note || ' '); // Allow empty text
      formData.append('date', isoDate);
      formData.append('employeeId', String(employeeId));
      formData.append('image', selectedImage);

      employeeDayNotesApi.createWithImage({
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { departmentId, seasonId }
      }).then(() => {
        fetchData();
        setDisableHover(true);
        setNote('');
        removeImage();
      }).catch((error) => {
        console.error('Error creating employee day note with image:', error);
      });
    } else {
      employeeDayNotesApi.create(
        { date: isoDate, text: note || ' ', employeeId },
        authParams
      ).then(() => {
        fetchData();
        setDisableHover(true);
      });
      setNote('');
    }
  };

  const editNote = (noteToEdit: EmployeeDayNoteEntity) => {
    setNoteActionId(noteToEdit.id);
    setNote(noteToEdit.text);
    document.getElementById('employeeDayNoteInput')?.focus();
  };

  const updateNote = () => {
    if (noteActionId > 0) {
      employeeDayNotesApi.update(
        String(noteActionId),
        { text: note || ' ' },
        authParams
      ).then(() => {
        fetchData();
        setNote('');
        setNoteActionId(-1);
      });
    }
  };

  const resetEditModeNote = () => {
    setNote('');
    setNoteActionId(-1);
  };

  useEffect(() => {
    notesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  return (
    <Box mb={3}>
      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
        {t("employeeDayNotes.title")}
      </Typography>

      <Box
        position="relative"
        onMouseMove={() => { if (disableHover) setDisableHover(false); }}
        sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        {noteActionId > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(1px)',
              zIndex: 1,
            }}
          />
        )}

        {notes.length > 0 ? (
          <Scrollbar style={{ maxHeight: 240 }}>
            <Stack spacing={3} px={2} py={2}>
              {notes
                .slice()
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((n) => (
                  <div ref={notesEnd} key={n.id}>
                    <ShiftNotesBubble
                      noteInstance={n as any}
                      notesApi={employeeDayNotesApi}
                      fetchData={fetchData}
                      editNoteCallback={editNote}
                      disableHover={disableHover}
                      departmentId={departmentId}
                      seasonId={seasonId}
                    />
                  </div>
                ))}
            </Stack>
          </Scrollbar>
        ) : (
          <Typography variant="body2" color="text.secondary" px={2} py={2}>
            {t("employeeDayNotes.empty")}
          </Typography>
        )}

        {noteActionId > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(92, 84, 112, 0.3)' }}>
            <KeyboardArrowLeft fontSize="small" onClick={resetEditModeNote} style={{ cursor: 'pointer' }} />
            <Small fontWeight={600} fontSize={10}>{t('notes.edit')}</Small>
          </div>
        )}

        <Divider />

        {/* Image Preview */}
        {imagePreview && (
          <Box sx={{ p: 2, position: 'relative' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('notes.imagePreview')}:
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', border: '1px solid #e0e0e0' }}
              />
              <IconButton
                size="small"
                onClick={removeImage}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        <FlexBetween padding={1.5} gap={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <InputBase
              id="employeeDayNoteInput"
              fullWidth
              multiline
              placeholder={t('notes.inputPlaceholder')}
              sx={{ fontSize: 12, fontWeight: 500 }}
              value={note}
              onChange={handleNoteChange}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  noteActionId < 0 ? createNote() : updateNote();
                }
              }}
            />

            {noteActionId < 0 && (
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            )}

            {noteActionId < 0 && (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{ color: selectedImage ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <Image />
              </IconButton>
            )}
          </Box>

          <StyledIconButton
            onClick={() => { noteActionId < 0 ? createNote() : updateNote(); }}
            disabled={noteActionId >= 0}
          >
            <Send sx={{ color: noteActionId >= 0 ? 'gray' : 'primary.main' }} />
          </StyledIconButton>
        </FlexBetween>
      </Box>
    </Box>
  );
};

export default EmployeeDayNotesSection;
